import { useState, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  FormControl,
  TextField,
  Typography,
  IconButton,
  Stack,
  Alert,
  Divider,
  FormHelperText,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import JoditEditor from "jodit-react";
import { useDispatch } from "react-redux";
import { createBlog, updateBlog } from "../../redux/slices/blogsSlice";

// Constants
const MAX_IMAGE_SIZE = 1024 * 1024;
const MIN_IMAGE_SIZE = 1024;
const IMAGE_WIDTH = 830;
const IMAGE_HEIGHT = 449;

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  margin: theme.spacing(3),
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "16px 24px",
  position: "sticky",
  top: 0,
  zIndex: 1100,
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  marginBottom: "24px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
    marginBottom: 0,
  },
}));

const ImageDropZone = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 300,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const Tag = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  margin: "4px",
  padding: "4px 12px",
  backgroundColor: theme.palette.primary.light,
  borderRadius: "16px",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    marginLeft: "8px",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));

const TagsContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "8px 0",
});

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const UploadIconContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
});

const Form = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const editor = useRef(null);

  // Get editData from navigation state
  const editData = location.state?.editData;

  const [formData, setFormData] = useState({
    image: null,
    preview: editData?.image || null,
    blogTitle: editData?.heading || "",
    writtenBy: editData?.writtenBy || "",
    slugTitle: editData?.slug || "",
    description: editData?.description || "",
    seoTitle: editData?.seoTitle || "",
    seoDescription: editData?.seoDescription || "",
    seoKeywords: editData?.seoKeywords || "",
    status: editData?.status || "active",
  });

  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);

  // Editor Config
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      placeholder: "Enter organ description here...",
      height: 300,
      buttons: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "link",
        "image",
      ],
      width: "100%",
    }),
    []
  );

  // Function to generate slug
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-");
  };

  // Handlers
  const handleImageChange = (event) => {
    const file = event.target?.files?.[0] || event;
    if (!file) return;

    // Clear any existing notifications
    setNotification(null);

    // Check if file is actually a File/Blob object
    if (!(file instanceof Blob)) {
      setErrors((prev) => ({ ...prev, image: "Invalid file format" }));
      return;
    }

    // Validate file size (1KB to 1MB)
    if (file.size < MIN_IMAGE_SIZE || file.size > MAX_IMAGE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must be between 1KB and 1MB",
      }));
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      // Validate image dimensions
      if (img.width !== IMAGE_WIDTH || img.height !== IMAGE_HEIGHT) {
        setErrors((prev) => ({
          ...prev,
          image: `Image dimensions must be exactly ${IMAGE_WIDTH}px × ${IMAGE_HEIGHT}px`,
        }));
        URL.revokeObjectURL(img.src);
        return;
      }

      // If validation passes, update form data
      setFormData((prev) => ({
        ...prev,
        image: file,
        preview: img.src,
      }));
      setErrors((prev) => ({ ...prev, image: "" }));
    };

    img.onerror = () => {
      setErrors((prev) => ({ ...prev, image: "Invalid image file format" }));
      URL.revokeObjectURL(img.src);
    };
  };

  const handleClearImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      preview: null,
    }));
  };

  const handleSEODescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setFormData((prev) => ({
        ...prev,
        seoDescription: value,
      }));
      setErrors((prev) => ({
        ...prev,
        seoDescription: "",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        seoDescription: "Description cannot exceed 100 characters",
      }));
    }
  };

  // Update the handleKeywordChange function
  const handleKeywordChange = (e) => {
    const value = e.target.value;

    if (value.includes(",")) {
      const keywords = value
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);

      if (keywords.length <= 15) {
        setFormData((prev) => ({
          ...prev,
          seoKeywords: keywords.join(","),
        }));
        setErrors((prev) => ({ ...prev, seoKeywords: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          seoKeywords: "Cannot exceed 15 keywords",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        seoKeywords: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formDataObj = new FormData();

      // Map fields to match API requirements
      formDataObj.append("heading", formData.blogTitle.trim());
      formDataObj.append("writtenBy", formData.writtenBy.trim());
      formDataObj.append("slug", formData.slugTitle.trim());
      formDataObj.append("description", formData.description.trim());
      formDataObj.append("seoTitle", formData.seoTitle.trim());
      formDataObj.append("seoDescription", formData.seoDescription.trim());
      formDataObj.append("seoKeywords", formData.seoKeywords.trim());
      formDataObj.append("status", formData.status);

      if (formData.image instanceof Blob) {
        formDataObj.append("image", formData.image);
      }

      // Dispatch create or update based on editData
      const action = editData
        ? updateBlog({ id: editData._id, data: formDataObj })
        : createBlog(formDataObj);

      const result = await dispatch(action).unwrap();

      if (result) {
        navigate("/blogs", { replace: true });
      }
    } catch (err) {
      console.error("Form submission error:", err);
      setNotification({
        type: "error",
        message: err.message || "Operation failed",
      });
    }
  };

  const handleClose = () => {
    navigate("/blogs"); // Navigate back to list
  };

  return (
    <StyledContainer>
      <StyledHeader>
        <IconButton onClick={handleClose} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          {editData ? "Edit" : "Add New"} Blog
        </Typography>
      </StyledHeader>

      <Box
        sx={{
          backgroundColor: "common.white",
        }}
      >
        <form onSubmit={handleSubmit}>
          {/* Image Upload Section */}
          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              Blog Image
            </Typography>
            <ImageDropZone
              onClick={() => document.getElementById("image-input").click()}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const file = e.dataTransfer.files[0];
                if (file) handleImageChange(file);
              }}
            >
              {formData.preview ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}
                >
                  <img
                    src={formData.preview}
                    alt="Organ Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <CloseButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearImage();
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </CloseButton>
                </Box>
              ) : (
                <UploadIconContainer>
                  <CloudUploadIcon
                    sx={{ fontSize: 64, color: "primary.main" }}
                  />
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    align="center"
                  >
                    Drag and drop your image here
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    align="center"
                  >
                    or click to browse
                  </Typography>
                  <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                    (Image must be 830px × 449px and between 1KB-1MB)
                  </Typography>
                </UploadIconContainer>
              )}
            </ImageDropZone>
            <input
              id="image-input"
              type="file"
              accept="image/*"
              onChange={(e) => handleImageChange(e)}
              style={{ display: "none" }}
            />
            {errors.image && (
              <FormHelperText error sx={{ ml: 1 }}>
                {errors.image}
              </FormHelperText>
            )}
          </StyledFormSection>

          {/* Basic Information Section */}
          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="Blog Title"
                required
                value={formData.blogTitle}
                onChange={(e) => {
                  const title = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    blogTitle: title,
                    slugTitle: generateSlug(title), // Automatically generate slug
                  }));
                }}
              />

              <TextField
                fullWidth
                label="Blog Writer Name"
                required
                value={formData.writtenBy}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    writtenBy: e.target.value,
                  }))
                }
              />

              <TextField
                fullWidth
                label="Blog Slug Title"
                required
                value={formData.slugTitle}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    slugTitle: e.target.value,
                  }))
                }
              />

              <FormControl fullWidth required>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  id="status-select"
                  value={formData.status}
                  label="Status"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </StyledFormSection>

          {/* Description Section */}
          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              Blog Description
            </Typography>
            <Box
              sx={{
                width: "100%",
                "& .jodit-container": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-workplace": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-wysiwyg": {
                  width: "100% !important",
                  maxWidth: "100%",
                  padding: "16px",
                },
                "& .jodit-toolbar__box": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-status-bar": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
              }}
            >
              <JoditEditor
                ref={editor}
                value={formData.description}
                config={editorConfig}
                onBlur={(newContent) => {
                  setFormData((prev) => ({
                    ...prev,
                    description: newContent,
                  }));
                }}
              />
            </Box>
          </StyledFormSection>

          {/* SEO Section */}
          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              SEO Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="SEO Title"
                value={formData.seoTitle}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    seoTitle: e.target.value,
                  }))
                }
              />

              <TextField
                fullWidth
                label="SEO Description"
                multiline
                rows={3}
                value={formData.seoDescription}
                onChange={handleSEODescriptionChange}
                error={Boolean(errors.seoDescription)}
                helperText={
                  errors.seoDescription ||
                  `${formData.seoDescription.length}/100 characters`
                }
              />

              <StyledFormSection>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    label="SEO Keywords"
                    variant="outlined"
                    value={formData.seoKeywords}
                    onChange={handleKeywordChange}
                    error={Boolean(errors.seoKeywords)}
                    helperText={
                      errors.seoKeywords ||
                      "Type keywords separated by commas. Example: keyword1,keyword2,keyword3 (max 15)"
                    }
                    placeholder="Type keywords separated by commas"
                  />

                  <TagsContainer>
                    {formData.seoKeywords.split(",").map((keyword, index) =>
                      keyword.trim() ? (
                        <Tag key={index}>
                          <Typography variant="body2" component="span">
                            {keyword.trim()}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const keywords = formData.seoKeywords
                                .split(",")
                                .filter((_, i) => i !== index)
                                .join(",");
                              setFormData((prev) => ({
                                ...prev,
                                seoKeywords: keywords,
                              }));
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tag>
                      ) : null
                    )}
                  </TagsContainer>
                </FormControl>
              </StyledFormSection>
            </Box>
          </StyledFormSection>

          {/* Submit Buttons */}
          {/* Submit Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Stack direction="row" spacing={2}>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            </Stack>
          </Box>
        </form>

        {notification && (
          <Alert severity={notification.type} sx={{ mt: 2 }}>
            {notification.message}
          </Alert>
        )}
      </Box>
    </StyledContainer>
  );
};

export default Form;
